/**
 * Shared zoom limits and follow-mode preference for board camera (follow and manual).
 * Single source of truth so UI, saga, storage, and reducer stay in sync.
 *
 * Follow mode uses **stops** (exposure-style): each step multiplies perceived zoom by a
 * fixed ratio so +/- feels consistent across different auto-fit baselines. A soft comfort
 * cap prevents over-zooming when content is compact (high auto-fit scale).
 */

export const MIN_SCALE = 0.4;
export const MAX_SCALE = 2.5;

/** Octave-style stops: multiplier = 2^(stops / FOLLOW_ZOOM_STOPS_PER_OCTAVE). */
export const FOLLOW_ZOOM_STOPS_PER_OCTAVE = 12;

/** Default: slightly zoomed out vs neutral (0) for a wider default framing. */
export const DEFAULT_FOLLOW_ZOOM_STOPS = -4;

export const MIN_FOLLOW_ZOOM_STOPS = -36;
export const MAX_FOLLOW_ZOOM_STOPS = 36;

/** One +/- click in follow mode. */
export const FOLLOW_ZOOM_STOP_STEP = 1;

/**
 * When raw auto-fit scale exceeds this, compress excess zoom (see soft compression). Slightly
 * below typical “tight cluster” fits so comfort engages before it feels too close.
 */
export const COMFORT_FIT_THRESHOLD = 1.08;

/** Anchor for soft compression in compact scenes; slightly below 1.42 for a wider read. */
export const COMFORT_FOLLOW_MAX_SCALE = 1.36;

/**
 * When raw auto-fit is high, scale above {@link COMFORT_FOLLOW_MAX_SCALE} is compressed by this
 * factor instead of a hard clamp. A hard min to a fixed scale caused a plateau: candidates
 * above that scale looked identical, so +/- stops appeared to do nothing.
 */
export const COMFORT_SOFT_COMPRESSION = 0.4;

/** @deprecated Use FOLLOW_ZOOM_STOP_STEP / follow-zoom stops in follow mode. */
export const ZOOM_STEP = 0.05;

/**
 * Multiplier applied to content bbox for zoom-to-fit (breathing room). Higher = more margin
 * around the action bbox and a more zoomed-out framing.
 */
export const FIT_PADDING_FACTOR = 1.68;

/**
 * Follow-camera speed tuning: translate+zoom travel -> spring profile.
 * Small travel stays snappy; larger jumps get slower and easier to track.
 */
export const CAMERA_TRAVEL_MIN = 0.02;
export const CAMERA_TRAVEL_MAX = 0.85;
export const CAMERA_ZOOM_TRAVEL_WEIGHT = 0.55;
export const CAMERA_FAST_TENSION = 128;
export const CAMERA_SLOW_TENSION = 72;
export const CAMERA_FAST_FRICTION = 20;
export const CAMERA_SLOW_FRICTION = 31;

/**
 * Saga low-pass: suppress micro camera retargets that only add spring churn.
 * Position in board-normalized units; scale in absolute zoom factor.
 */
export const CAMERA_POSITION_EPS = 0.002;
export const CAMERA_SCALE_EPS = 0.008;

export interface CameraTarget {
  x: number;
  y: number;
  scale: number;
}

/**
 * Maps legacy persisted multiplier (0.4–2.5) to integer stops for migration.
 * Neutral legacy factor ~1.0 maps to 0 stops.
 */
export function mapLegacyFollowZoomFactorToStops(factor: number): number {
  if (!Number.isFinite(factor) || factor <= 0) return DEFAULT_FOLLOW_ZOOM_STOPS;
  const raw = Math.round(FOLLOW_ZOOM_STOPS_PER_OCTAVE * Math.log2(factor));
  return clampInt(raw, MIN_FOLLOW_ZOOM_STOPS, MAX_FOLLOW_ZOOM_STOPS);
}

export function clampFollowZoomStops(stops: number): number {
  if (!Number.isFinite(stops)) return DEFAULT_FOLLOW_ZOOM_STOPS;
  return clampInt(Math.round(stops), MIN_FOLLOW_ZOOM_STOPS, MAX_FOLLOW_ZOOM_STOPS);
}

export function followZoomMultiplierFromStops(stops: number): number {
  return Math.pow(2, stops / FOLLOW_ZOOM_STOPS_PER_OCTAVE);
}

/**
 * Bootstrap / idle scale from stops alone (assumes unit baseline), for initial Redux state
 * and first-frame spring before saga runs.
 */
export function bootstrapScaleFromFollowStops(stops: number): number {
  const m = followZoomMultiplierFromStops(clampFollowZoomStops(stops));
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, m));
}

/**
 * Hybrid follow scale: raw auto-fit × perceptual multiplier, then soft comfort cap when
 * auto-fit is high (compact content).
 */
export function computeFollowScaleFromFit(rawFitScale: number, stops: number): number {
  if (!Number.isFinite(rawFitScale) || rawFitScale <= 0) {
    return MIN_SCALE;
  }
  const mult = followZoomMultiplierFromStops(clampFollowZoomStops(stops));
  const candidate = rawFitScale * mult;
  let scale = candidate;
  if (rawFitScale > COMFORT_FIT_THRESHOLD && candidate > COMFORT_FOLLOW_MAX_SCALE) {
    const excess = candidate - COMFORT_FOLLOW_MAX_SCALE;
    scale = COMFORT_FOLLOW_MAX_SCALE + excess * COMFORT_SOFT_COMPRESSION;
  }
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
}

export interface CameraSpringProfile {
  tension: number;
  friction: number;
}

/**
 * Distance metric for camera retargeting.
 * Pan distance and zoom delta (in stops/octaves) are combined into one scalar.
 */
export function computeCameraTravelMetric(
  from: { x: number; y: number; scale: number },
  to: { x: number; y: number; scale: number },
): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const panDistance = Math.hypot(dx, dy);

  const safeFromScale = Math.max(from.scale, 0.0001);
  const safeToScale = Math.max(to.scale, 0.0001);
  const zoomDistance = Math.abs(Math.log2(safeToScale / safeFromScale));

  return panDistance + CAMERA_ZOOM_TRAVEL_WEIGHT * zoomDistance;
}

/**
 * Non-linear mapping from travel metric to follow-camera spring.
 * sqrt-based curve slows large jumps without making small updates feel heavy.
 */
export function mapTravelToCameraSpring(travel: number): CameraSpringProfile {
  const normalized = clamp01(
    (travel - CAMERA_TRAVEL_MIN) / (CAMERA_TRAVEL_MAX - CAMERA_TRAVEL_MIN),
  );
  const eased = Math.sqrt(normalized);
  return {
    tension: lerp(CAMERA_FAST_TENSION, CAMERA_SLOW_TENSION, eased),
    friction: lerp(CAMERA_FAST_FRICTION, CAMERA_SLOW_FRICTION, eased),
  };
}

/**
 * Returns true when saga should skip dispatching moveCamera (latest-target policy:
 * interrupt only when change is perceptible).
 */
export function shouldSkipCameraRetarget(current: CameraTarget, next: CameraTarget): boolean {
  const dx = next.x - current.x;
  const dy = next.y - current.y;
  if (Math.hypot(dx, dy) > CAMERA_POSITION_EPS) return false;
  if (Math.abs(next.scale - current.scale) > CAMERA_SCALE_EPS) return false;
  return true;
}

function clampInt(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
